import z from "zod";

// The bulk "Update Region" form, kept in a module with NO component or client imports so it can be
// unit-tested directly (the test runner cannot resolve the .css imports that arrive transitively
// with anything importing @/client). Same arrangement as components/custom/regions/-create-region-schema.ts.
//
// Regions are assigned BY ID from the regions tables (ID-7057 phase 2). Absent = leave unchanged,
// null = clear -- the same contract as PATCH /sites/regions itself.

// A FACTORY, because the message is shown to the user and this app has no z.setErrorMap.
// `clear` is the explicit "remove the assignment" instruction. A cleared SELECT yields undefined
// ("leave unchanged"), so without it the dialog could assign but never unassign -- found by the
// live pass: clearing the region re-triggered "choose a region" and Update stayed disabled.
export const regionBulkFormSchema = (t: (key: string) => string) =>
  z
    .object({
      regionId: z.number().int().positive().nullable().optional(),
      subRegionId: z.number().int().positive().nullable().optional(),
      clear: z.boolean().optional(),
    })
    .refine(
      (values) =>
        values.clear === true ||
        values.regionId !== undefined ||
        values.subRegionId !== undefined,
      { message: t("atLeastOneFieldRequired"), path: ["regionId"] },
    );

export type RegionBulkFormValues = z.infer<
  ReturnType<typeof regionBulkFormSchema>
>;

/**
 * The PATCH /sites/regions body. A region chosen with no child sends `subRegionId: null`
 * explicitly: the server would clear a stranded sub-region anyway, but a request that says what it
 * does is easier to read in a log than one that relies on the server's silent rule. A sub-region
 * alone is sent alone -- the server sets its region with it.
 */
export const toUpdateRegionsBody = <Query>(
  values: RegionBulkFormValues,
  query: Query,
) =>
  values.clear
    ? { regionId: null, subRegionId: null, query }
    : {
        regionId: values.regionId,
        subRegionId:
          values.regionId !== undefined && values.subRegionId === undefined
            ? null
            : values.subRegionId,
        query,
      };

/**
 * Changing or clearing the region invalidates a sub-region picked under the previous one; keeping
 * the same region keeps it. Pure so the cascade is testable without the select components.
 */
export const onRegionChange = (
  values: RegionBulkFormValues,
  regionId: number | null | undefined,
): RegionBulkFormValues => ({
  regionId,
  subRegionId: regionId === values.regionId ? values.subRegionId : undefined,
});
